import { useState } from "react";
import { Tooltip } from "@mui/material";
import DSSwitch from "../../system-design/DSSwitch";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSTextField from "../../system-design/DSTextField";
import DSTypography from "../../system-design/DSTypography";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import useSetUnlistedMutation from "../hooks/useSetUnlistedMutation";
import { extractApiErrorMessage } from "../../../utils/util";

/**
 * The Unlist switch (a ban). Turning it ON asks for an optional note for the
 * artist: the studio shows "Banned" with that note instead of the review status.
 * Turning it OFF lifts the ban and clears the note. The review status is never
 * changed by this switch.
 */
const UnlistSwitch = ({
  klydoId,
  unlisted,
  disabledReason,
}: {
  klydoId: string;
  unlisted: boolean;
  disabledReason?: string;
}) => {
  const [target, setTarget] = useState<boolean | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const { mutate, isPending } = useSetUnlistedMutation(klydoId);

  const open = (next: boolean) => {
    setMessage("");
    setError("");
    setTarget(next);
  };
  const close = () => {
    if (!isPending) setTarget(null);
  };

  const confirm = () => {
    if (target === null) return;
    mutate(
      { unlisted: target, message: target ? message.trim() : undefined },
      {
        onSuccess: () => setTarget(null),
        onError: (e) =>
          setError(extractApiErrorMessage(e, "Failed to update the Klydo")),
      },
    );
  };

  return (
    <div style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
      <Tooltip title={disabledReason || ""} placement="top" arrow>
        <span>
          <DSSwitch
            checked={unlisted}
            onChange={(e) => open(e.target.checked)}
            disabled={!!disabledReason}
          />
        </span>
      </Tooltip>

      <DSDialog className="modal-medium" open={target !== null} onClose={close}>
        <DSDialogTitle>
          {target ? "Unlist this Klydo?" : "Remove the unlist?"}
        </DSDialogTitle>
        <DSDialogContent>
          {target ? (
            <>
              <DSTypography variant="body2">
                This removes the Klydo from all clocks. The artist will see it
                as "Banned" in the studio, with your message. Its review status
                does not change.
              </DSTypography>
              <DSTextField
                fullWidth
                multiline
                rows={4}
                margin="normal"
                label="Message to the artist (optional)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </>
          ) : (
            <DSTypography variant="body2">
              This makes the Klydo available for all actions again (pool |
              public). The artist's "Banned" status and message are removed. The
              review status does not change.
            </DSTypography>
          )}
          {error && (
            <DSTypography variant="body2" color="error">
              {error}
            </DSTypography>
          )}
        </DSDialogContent>
        <DSDialogActions>
          <DSSecondaryButton disabled={isPending} onClick={close}>
            Cancel
          </DSSecondaryButton>
          {isPending && <DSCircularProgress size={20} />}
          <DSPrimaryButton disabled={isPending} onClick={confirm}>
            Confirm
          </DSPrimaryButton>
        </DSDialogActions>
      </DSDialog>
    </div>
  );
};

export default UnlistSwitch;
